import React, { useState, useEffect, useContext } from 'react';
import { Box, Typography, FormHelperText, useTheme } from '@material-ui/core';
import { styled, Theme } from '@material-ui/core/styles';
import gql from 'not-graphql-tag';

import Grid from '@material-ui/core/Grid';
import colors from '../../../themes/main-colors';

import { navigate, useLocation } from '@reach/router';
import * as queryString from 'query-string';
import routes from '../../../routes';

import useContentMgmt from '../../../hooks/useContentMgmt';
import { useEVChargerFormState } from '../../../providers/EVChargerFormStateProvider';
import AccountSearch from '../../account-search/AccountSearch';
import { ProgramCard } from '../../pge-plus-common/ProgramCard';
import useAccountListHeader from '../../account-summary/multi-accounts/useAccountListHeader';
import useAccountDetailList from '../../../hooks/useAccountDetailList';
import {
  getAccounts,
  getApplicationsByAccountQueryWithAddress,
  getAccountDetails,
} from '../../pge-plus-common/queries/ChooseAccount';
import Backdrop from '../../backdrop';
import {
  AccountRelationship,
  AccountDetail,
  AccountDetailList,
  AccountDetailParams,
} from '../../../__generated__/pge-types';
import useAccountCustomer from '../../../hooks/useAccountCustomer';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import useEVApplicationApi from '../../../hooks/pge-plus/useEVApplicationApi';
import {
  Application,
  ApplicationEv,
  ApplicationStatus,
  ApplicationType,
  IneligibilityReasonCode,
  ApplicationEvSectionType,
  SectionEvAccountInfo,
} from '../../../__generated__/pgeplus-types';
import useUtil from '../../../hooks/pge-plus/useUtil';
import { IneligibilityTypeNumber } from '../Ineligible';
import useWrapWithLoader from '../../../hooks/useWrapWithLoading';
import BYOCConfirmModal from './BYOCConfirmModal';
import useAuthQuery from '../../../hooks/useAuthQuery';
const ITEMS_PER_PAGE = 6;

interface ChooseAccountProps {
  children?: React.ReactNode;
  path: string;
}
type QueryParams = {
  suppliedByCustomer?: string;
  showOnlyInProgressAcc?: string;
};

const PREFIX = 'EVChargerChooseAccount';
const classes = {
  accountCard: `${PREFIX}-accountCard`,
  showMoreAccounts: `${PREFIX}-showMoreAccounts`,
  cardSearch: `${PREFIX}-cardSearch`,
};

const StyledGrid = styled(Grid)(({ theme }: { theme: Theme }) => ({
  [`& .${classes.accountCard}`]: {
    padding: theme.spacing(3, 3),
    minHeight: '125px',
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.showMoreAccounts}`]: {
    fontSize: '18px',
    lineHeight: '30px',
    color: colors.sparkBlue,
    textAlign: 'center',
    cursor: 'pointer',
    fontWeight: '700',
  },
  [`& .${classes.cardSearch}`]: {
    marginBottom: '5px',
    zIndex: 100,
  },
}));

export const ChooseAccount: React.FC<ChooseAccountProps> = props => {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = queryString.parse(location.search) as QueryParams;

  const { accountListParams, onAccountSearch } = useAccountListHeader();
  const [showAccountByLimit, setShowAccountByLimit] = useState(ITEMS_PER_PAGE);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { accounts, totalCount, loading } = useAccountDetailList(
    getAccounts,
    'RES_ACCTS',
    {
      accountListParams: {
        ...accountListParams,
        pageSize: showAccountByLimit,
        userAccountRelFilter: [
          AccountRelationship.Main,
          AccountRelationship.Coapp,
        ],
      },
    },
  );

  const { setErrorNotification } = useUtil();
  const { wrapWithLoader } = useWrapWithLoader();
  const [showBYOCConfirmModal, setShowBYOCConfirmModal] = useState<boolean>(
    false,
  );
  const [accountAndPremise, setAccountAndPremise] = useState<{
    account: AccountDetail;
    premise: string;
  }>();
// Note: Added resetForm as an additional safety guard
  const { updateFormState, initFormStateAndNavigate, resetForm} = useEVChargerFormState();

  const {
    createApplication,
    getApplicationDetails,
    getApplicationsByAccountWithDetails,
  } = useEVApplicationApi();

  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_CHOOSE_ACCOUNT);
  const [inProgressApplications, setInProgressApplications] = useState<
    Application[]
  >([]);

  const shouldShowInProgressAccOnly =
    queryParams.showOnlyInProgressAcc === 'true';

  //Get accounts
  const { customer, loading: loadingCustomer } = useAccountCustomer();

  const accountNumbers = inProgressApplications.map(application => ({
    accountNumber: application.accountId,
  }));

  const {
    data: accountDetailsList,
    loading: accountDetailsLoading,
  } = useAuthQuery<
    {
      getAccountDetails: Array<AccountDetail>;
      getAccountDetailList: AccountDetailList;
    },
    {
      params: AccountDetailParams;
    }
  >(getAccountDetails, {
    skip: !Boolean(accountNumbers.length) || !shouldShowInProgressAccOnly,
    variables: {
      params: {
        accountNumberList: accountNumbers.map(({ accountNumber }) => ({
          accountNumber,
        })),
        paging: {
          limit: showAccountByLimit,
          offset: 0,
        },
      },
    },
    onCompleted: data => {
      const accountDetails = data?.getAccountDetails;
      if (
        shouldShowInProgressAccOnly &&
        accountDetails?.length === 1 &&
        accountDetails[0] &&
        accountDetails[0].premiseInfo &&
        accountDetails[0].premiseInfo[0] &&
        accountDetails[0].premiseInfo[0].encryptedPremiseId
      ) {
        void handleSelectedAccount(
          accountDetails[0],
          accountDetails[0].premiseInfo[0].encryptedPremiseId,
          isAccountHasPendingApplication(
            accountDetails[0].accountNumber,
            accountDetails[0].premiseInfo[0].addressLine1,
            inProgressApplications,
          ),
        );
      }
    },
  });

  const accountsList =
    (shouldShowInProgressAccOnly
      ? accountDetailsList?.getAccountDetails
      : accounts) || [];

  useEffect(() => {
    if (shouldShowInProgressAccOnly) {
      void getInProgressApplications();
    }
  }, []);

  useEffect(() => {
    // user is ineligible if no accounts found
    const hasNoAccountWithPremise = !Boolean(
      accounts.find(account => account.premiseInfo !== null),
    );
    if (
      !loading &&
      ((!shouldShowInProgressAccOnly &&
        accounts.length === 0 &&
        !accountListParams.query) ||
        hasNoAccountWithPremise)
    ) {
      void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
        state: {
          IneligibilityCode: IneligibilityReasonCode.IneligibleC2M,
          IneligibilityNumber: IneligibilityTypeNumber.NOT_ACTIVE,
          fromPage: routes.PGE_PLUS_EV_CHARGERS_CHOOSE_ACCOUNT,
        },
      });
    }

    if (!shouldShowInProgressAccOnly && accounts.length > 0 && customer) {
      void getInProgressApplications();
    }
  }, [accounts.length, customer, loading]);

  const getInProgressApplications = wrapWithLoader(async () => {
    try {
      const variables: any = {
        applicationType: ApplicationType.Ev,
        status: [
          ApplicationStatus.Created,
          ApplicationStatus.InProgress,
          ApplicationStatus.Submitted,
          ApplicationStatus.Servicing,
        ],
      };

      if (!shouldShowInProgressAccOnly) {
        const accountNumbersToInclude = accounts.map(
          account => account.accountNumber,
        );
        variables['accountId'] = accountNumbersToInclude;
      }

      const { data } = await getApplicationsByAccountWithDetails(
        variables,
        getApplicationsByAccountQueryWithAddress,
      );

      //Auto select single account
      if (
        !shouldShowInProgressAccOnly &&
        accountListParams.query === '' &&
        totalCount === 1 &&
        accounts[0].premiseInfo &&
        accounts[0].premiseInfo?.length === 1 &&
        accounts[0].premiseInfo[0] &&
        customer?.personId
      ) {
        const urlParams: any = new URLSearchParams(window.location.search);
        const isBYOCConfirmed = urlParams.get('suppliedByCustomer');
        const appInProgress = isAccountHasPendingApplication(
          accounts[0].accountNumber,
          accounts[0].premiseInfo[0].addressLine1,
          data.getApplications.applications,
        );
        if (isBYOCConfirmed || appInProgress) {
          void handleSelectedAccount(
            accounts[0],
            accounts[0].premiseInfo[0].encryptedPremiseId,
            isAccountHasPendingApplication(
              accounts[0].accountNumber,
              accounts[0].premiseInfo[0].addressLine1,
              data.getApplications.applications,
            ),
          );
        }
      }

      setInProgressApplications(data.getApplications.applications);
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const isAccountHasPendingApplication = (
    accountId: string,
    address: string,
    inProgressApps?: Application[],
  ) => {
    const apps = inProgressApps || inProgressApplications;
    //Finding pending application by account and address line
    const foundApplicationByAddress = apps.filter(acc => {
      const AccountInfoSection = (acc as ApplicationEv).details.find(
        detail => detail.sectionType === ApplicationEvSectionType.EvAccountInfo,
      );

      let addressLine = '';

      if (AccountInfoSection) {
        addressLine =
          (AccountInfoSection as SectionEvAccountInfo).data.address
            ?.addressLine1 || '';
      }

      return (
        acc.accountId === accountId && address.trim() === addressLine.trim()
      );
    });

    return foundApplicationByAddress.length > 0;
  };

  const handleSelectedAccount = async (
    acc: AccountDetail,
    encryptedPremiseId: string,
    hasExistingApplication: boolean,
  ) => {
    try {
      setIsLoading(true);
      const urlParams: any = new URLSearchParams(window.location.search);
      const isBYOCConfirmed = urlParams.get('suppliedByCustomer');

      if (!hasExistingApplication && !isBYOCConfirmed) {
        setAccountAndPremise({
          account: acc,
          premise: encryptedPremiseId,
        });
        setShowBYOCConfirmModal(true);
        setIsLoading(false);
        return;
      } else {
        const { data } = await createApplication({
          personId: customer?.encryptedPersonId,
          premiseId: encryptedPremiseId,
          accountId: acc?.accountNumber,
          suppliedByCustomer:
            queryParams.suppliedByCustomer &&
            queryParams.suppliedByCustomer === 'true'
              ? true
              : false,
        });

        if (
          data?.createApplicationEV?.ineligibilityReasons &&
          data?.createApplicationEV?.ineligibilityReasons.length > 0
        ) {
          const ineligibilityReason =
            data.createApplicationEV.ineligibilityReasons[0];

          if (
            ineligibilityReason.code ===
              IneligibilityReasonCode.IneligibleApplicationInProgress ||
            ineligibilityReason.code ===
              IneligibilityReasonCode.IneligibleApplicationLimit
          ) {
            void navigate(routes.PGE_PLUS_EV_CHARGERS_WELCOME_BACK, {
              state: { accountNumber: acc?.accountNumber },
            });
          } else {
            void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
              state: {
                IneligibilityCode:
                  data?.createApplicationEV?.ineligibilityReasons[0]?.code,
                IneligibilityNumber:
                  data?.createApplicationEV?.ineligibilityReasons[0]?.reference
                    ?.number,
                fromPage: routes.PGE_PLUS_EV_CHARGERS_CHOOSE_ACCOUNT,
              },
            });
          }

          // if (applicationInProgress.length > 0) {
          //   if (applicationInProgress[0]?.details?.application?.applicationNo) {
          //     //Fetch the details of the exising application
          //     const {
          //       data: existingApplicationData,
          //     } = await getApplicationDetails({
          //       applicationId:
          //         applicationInProgress[0]?.details?.application?.applicationNo,
          //     });

          //     if (
          //       existingApplicationData?.getApplication?.application
          //         ?.applicationType === ApplicationType.Er
          //     ) {
          //       void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
          //         state: {
          //           IneligibilityCode:
          //             data?.createApplicationEV?.ineligibilityReasons[0]?.code,
          //           IneligibilityNumber:
          //             data?.createApplicationEV?.ineligibilityReasons[0]
          //               ?.reference?.number,
          //           fromPage: routes.PGE_PLUS_EV_CHARGERS_CHOOSE_ACCOUNT,
          //         },
          //       });
          //     } else {
          //       const appStatus =
          //         existingApplicationData?.getApplication?.application?.status;
          //       if (
          //         appStatus === ApplicationStatus.Expired ||
          //         appStatus === ApplicationStatus.Submitted ||
          //         appStatus === ApplicationStatus.Canceled ||
          //         appStatus === ApplicationStatus.Rejected ||
          //         appStatus === ApplicationStatus.Completed
          //       ) {
          //         void navigate(routes.PGE_PLUS_EV_CHARGERS_WELCOME_BACK, {
          //           state: { accountNumber: acc?.accountNumber },
          //         });
          //         return;
          //       }

          //       updateFormState('account', acc);
          //       initFormStateAndNavigate(
          //         existingApplicationData?.getApplication
          //           ?.application as ApplicationEv,
          //       );
          //     }
          //   } else {
          //     setErrorNotification(true);
          //     setIsLoading(false);
          //     return;
          //   }
          // } else {
          //   void navigate(routes.PGE_PLUS_EV_CHARGERS_INELIGIBLE, {
          //     state: {
          //       IneligibilityCode:
          //         data?.createApplicationEV?.ineligibilityReasons[0]?.code,
          //       IneligibilityNumber:
          //         data?.createApplicationEV?.ineligibilityReasons[0]?.reference
          //           ?.number,
          //       fromPage: routes.PGE_PLUS_EV_CHARGERS_CHOOSE_ACCOUNT,
          //     },
          //   });

          //   setIsLoading(false);
          //   return;
          // }
        } else if (data?.createApplicationEV?.application) {
          //Update the selected account Form State
          updateFormState('account', acc);
          resetForm();
          initFormStateAndNavigate(data?.createApplicationEV?.application);
        } else {
          setErrorNotification(true);
          setIsLoading(false);
        }
      }

      setIsLoading(false);
      return;
    } catch (e) {
      setErrorNotification(true);
      setIsLoading(false);
    }
  };

  return (
    <StyledGrid>
      {(loading || isLoading || accountDetailsLoading) && (
        <Backdrop forceOpen />
      )}
      <Box display={'flex'}>
        <Typography
          component="h1"
          variant="h1"
          css={{
            textAlign: 'center',
            fontWeight: 700,
            lineHeight: '43px !important',
          }}
        >
          {content.get('CHOOSE_ACCOUNT_TITLE')}
        </Typography>
      </Box>
      <Box
        css={{ marginBottom: theme.spacing(5), padding: theme.spacing(0, 2) }}
      >
        <Box css={{ marginTop: theme.spacing(2) }}>
          <Typography
            variant="h6"
            gutterBottom
            css={{
              color: colors.noirBlurLight,
              fontSize: '20px !important',
              lineHeight: '26px !important',
              fontFamily: 'Untitled-Sans',
            }}
          >
            {content.get('CHOOSE_ACCOUNT_DESCRIPTION')}
          </Typography>
        </Box>
        <Box css={{ marginTop: theme.spacing(3) }}>
          <AccountSearch
            onSearch={(search: string) => {
              onAccountSearch(search);
            }}
            value={accountListParams.query}
            styleClass={classes.cardSearch}
            hideSearchIcon={true}
            maxLength={10}
          />
        </Box>
        {!loading && accountsList.length === 0 && accountListParams.query && (
          <Box>
            <Typography variant="body1" css={{ color: colors.roseRed }}>
              {content.get('NO_ACCOUNTS_FOUND_ERROR_TEXT')}
            </Typography>
          </Box>
        )}
        <Grid container direction="column">
          <Grid container item spacing={2}>
            {accountsList?.slice(0, showAccountByLimit).map(account => {
              return account?.premiseInfo?.map((premise, idx) => (
                <Grid item xs={12} md={6}>
                  <Box css={{ marginTop: theme.spacing(3) }} key={idx}>
                    <ProgramCard
                      key={`${account.accountNumber} - card`}
                      className={classes.accountCard}
                      title={premise?.addressLine1}
                      description={
                        <>
                          {content.get('ACCOUNT')}:{' '}
                          <strong>{account.accountNumber}</strong>
                          {isAccountHasPendingApplication(
                            account.accountNumber,
                            premise?.addressLine1!,
                          ) ? (
                            <FormHelperText error>
                              {content.get(
                                'EXISTING_APPLICATION_IN_PROGRESS_LABEL',
                              )}
                            </FormHelperText>
                          ) : null}
                        </>
                      }
                      onClick={() =>
                        handleSelectedAccount(
                          account,
                          premise?.encryptedPremiseId || '',
                          isAccountHasPendingApplication(
                            account.accountNumber,
                            premise?.addressLine1!,
                          ),
                        )
                      }
                    />
                  </Box>
                </Grid>
              ));
            })}
          </Grid>
        </Grid>

        {accountsList?.length >= ITEMS_PER_PAGE &&
          accountsList?.length !== totalCount && (
            <Box css={{ marginTop: theme.spacing(2) }}>
              <Typography
                variant="subtitle1"
                component="div"
                className={classes.showMoreAccounts}
                onClick={() =>
                  setShowAccountByLimit(showAccountByLimit + ITEMS_PER_PAGE)
                }
              >
                {content.get('SHOW_MORE_ACCOUNTS')}
              </Typography>
            </Box>
          )}
      </Box>
      <BYOCConfirmModal
        content={content}
        open={showBYOCConfirmModal}
        onClose={() => setShowBYOCConfirmModal(false)}
        createApplication={createApplication}
        personId={customer?.encryptedPersonId || ''}
        premiseId={accountAndPremise?.premise || ''}
        account={accountAndPremise?.account}
        updateFormState={updateFormState}
        initFormStateAndNavigate={initFormStateAndNavigate}
      />
    </StyledGrid>
  );
};
